import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { GetIndicatorsRequest, SalesInsightsResponse, useSalesInsights } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateTimeParam } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import NumericChart from '../charts/numericChart/NumericChart';

const SalesInsightAreaHospitality = () => {
    const { t } = useTranslation();
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);
    const [totalDevices, setTotalDevices] = useState(0);
    const [totalStaff, setTotalStaff] = useState(0);

    useEffect(() => {
        setTotalDevices(companyConfig?.devices?.length || 0);
        setTotalStaff(companyConfig?.staff?.length || 0);
    }, [companyConfig])


    const [params, setParams] = useState<GetIndicatorsRequest>({
        fields: 'totalSales,totalTax,netSales,transactionVolume,avgTransactionValue,activeStaff,activeDevices,totalDiscount,totalServiceCharge,netMargin',
        startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
        endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id
    });

    const {
        data: salesInsights,
        isLoading,
        isError,
        refetch: fetchSalesInsights
    } = useSalesInsights<SalesInsightsResponse>(params, 'sales-insights');

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsights();
        }
    }, [refreshDate]);

    let salesPercentage: number | undefined;
    if (salesInsights?.salesInsights?.totalTax && salesInsights?.salesInsights?.totalSalesIncTax
        && salesInsights?.salesInsights?.totalSalesIncTax !== 0
    ) {
        salesPercentage = (salesInsights.salesInsights.totalTax / salesInsights.salesInsights.totalSalesIncTax);
    }

    let netSalesPercentage: number | undefined;
    if (salesInsights?.salesInsights?.totalTax && salesInsights?.salesInsights?.netSalesIncTax
        && salesInsights?.salesInsights?.netSalesIncTax !== 0
    ) {
        netSalesPercentage = (salesInsights.salesInsights.totalTax / salesInsights.salesInsights.netSalesIncTax);
    }

    let salesRevenuePercentage: number | undefined;
    if (salesInsights?.salesInsights?.totalTax && salesInsights?.salesInsights?.totalSalesIncTax
        && salesInsights?.salesInsights?.totalSalesIncTax !== 0
    ) {
        salesRevenuePercentage = (salesInsights.salesInsights.totalTax / salesInsights.salesInsights.totalSalesIncTax);
    }

    let activeStaffPercentage: number | undefined;
    if (salesInsights?.salesInsights?.activeStaff && totalStaff
        && totalStaff !== 0
    ) {
        activeStaffPercentage = (salesInsights.salesInsights.activeStaff / totalStaff);
    }

    let activeDevicePercentage: number | undefined;
    if (salesInsights?.salesInsights?.activeDevices && totalDevices
        && totalDevices !== 0
    ) {
        activeDevicePercentage = (salesInsights.salesInsights.activeDevices / totalDevices);
    }

    let discountPercentage: number | undefined;
    if (salesInsights?.salesInsights?.totalServiceCharge && salesInsights?.salesInsights?.totalDiscount
        && salesInsights?.salesInsights?.totalDiscount !== 0
    ) {
        discountPercentage = (salesInsights.salesInsights.totalServiceCharge / salesInsights.salesInsights.totalDiscount);
    }

    useEffect(() => {
        setParams({
            ...params,
            startDateTime: filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
            endDateTime: filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
            locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id
        });
    }, [filters]);

    useEffect(() => { fetchSalesInsights(); }, [params]);

    return (
        <Box sx={{ marginY: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <NumericChart
                    title={t("screens.dashboarding.charts.sales")}
                    subtitle={t("screens.dashboarding.charts.includingTax")}
                    alignment="double"
                    mainNumber={salesInsights?.salesInsights.totalSalesIncTax}
                    mainCurrency={salesInsights?.currency}
                    numbers={[
                        {
                            number: salesInsights?.salesInsights.totalTax,
                            description: t("screens.dashboarding.charts.totalTax"),
                            currency: salesInsights?.currency
                        },
                        {
                            number: salesPercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            currency: salesInsights?.currency,
                            showAsPercentage: true
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.transactions")}
                    subtitle={t("screens.dashboarding.charts.volume")}
                    alignment="single"
                    mainNumber={salesInsights?.salesInsights.transactionVolume}
                    numbers={[
                        {
                            number: salesInsights?.salesInsights.avgTransactionValue,
                            description: `${t("screens.dashboarding.charts.averageNetSales")} ${t("screens.dashboarding.charts.incTax")}`,
                            currency: salesInsights?.currency
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_OAUTH_CALLBACK_URL}transactions`}
                    buttonText={t("screens.dashboarding.charts.transactionsOverview")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.netSales")}
                    subtitle={t("screens.dashboarding.charts.excludingTax")}
                    alignment="empty"
                    mainNumber={salesInsights?.salesInsights.netSalesExcTax}
                    mainCurrency={salesInsights?.currency}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.netMargin")}
                    subtitle={t("screens.dashboarding.charts.excludingTax")}
                    alignment="double"
                    mainNumber={salesInsights?.salesInsights.netMargin}
                    mainCurrency={salesInsights?.currency}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.staff")}
                    subtitle={t("screens.dashboarding.charts.active")}
                    alignment="double"
                    mainNumber={salesInsights?.salesInsights.activeStaff}
                    numbers={[
                        {
                            number: totalStaff,
                            description: t("screens.dashboarding.charts.totalStaff"),
                        },
                        {
                            number: activeStaffPercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            showAsPercentage: true
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByEmployee.aspx`}
                    buttonText={t("screens.dashboarding.charts.netSalesByStaff")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.devices")}
                    subtitle={t("screens.dashboarding.charts.active")}
                    alignment="double"
                    mainNumber={salesInsights?.salesInsights.activeDevices}
                    numbers={[
                        {
                            number: totalDevices,
                            description: t("screens.dashboarding.charts.totalDevices"),
                        },
                        {
                            number: activeDevicePercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            showAsPercentage: true
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByDeviceGroup.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesByDevices")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.discounts")}
                    subtitle={t("screens.dashboarding.charts.includingTax")}
                    alignment="double"
                    mainNumber={salesInsights?.salesInsights.totalDiscount}
                    mainCurrency={salesInsights?.currency}
                    numbers={[
                        {
                            number: salesInsights?.salesInsights.totalServiceCharge,
                            description: t("screens.dashboarding.charts.serviceCharge"),
                            currency: salesInsights?.currency
                        },
                        {
                            number: discountPercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            currency: salesInsights?.currency,
                            showAsPercentage: true
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/ServiceCharges.aspx`}
                    buttonText={t("screens.dashboarding.charts.serviceCharge")}
                />

            </Box>
        </Box>
    )
}

export default SalesInsightAreaHospitality;
